import { useState, useEffect } from "react";
import { RiCloseLargeFill, RiDeleteBinLine, RiAddFill } from "react-icons/ri";
import { FaUserPlus } from "react-icons/fa";
import DynamicSelect from "../../DynamicSelect";
import { toast } from "react-toastify";
import axios from "axios";
import { PrintBillLayout } from "../../PrintBillLayout";

// --- CONFIGURATION ---
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api";

const OutdoorBillModal = ({ isOpen, onClose, billId, onSave }) => {
  const [currentBillId, setCurrentBillId] = useState(billId);
  const [formData, setFormData] = useState({
    patientId: "",
    patientName: "",
    phoneNumber: "",
    admissionDate: "",
    billDate: new Date().toISOString().split("T")[0],
    refer: "",
    referAmount: "",
    billNumber: "",
    consultantName: "",
  });

  const [tableData, setTableData] = useState([
    { serviceName: "", unitPrice: 0, totalUnit: 1, amount: 0 },
  ]);

  // Totals State
  const [billResponse, setBillResponse] = useState({
    billAmount: 0,
    serviceChargePc: 0,
    serviceCharge: 0,
    totalAmount: 0,
    discountPc: 0,
    discount: 0,
    netAmount: 0,
    paidAmount: 0,
    payableAmount: 0,
  });

  // Individual inputs
  const [serviceCharge, setServiceCharge] = useState("0");
  const [discount, setDiscount] = useState("0");
  const [paidAmount, setPaidAmount] = useState("0");

  // UI States
  const [patients, setPatients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [errors, setErrors] = useState({});

  // New Patient Modal
  const [isNewPatientOpen, setIsNewPatientOpen] = useState(false);
  const [newPatientData, setNewPatientData] = useState({
    name: "",
    phoneNumber: "",
  });
  const [isAddingPatient, setIsAddingPatient] = useState(false);

  useEffect(() => {
    setCurrentBillId(billId);
  }, [billId]);

  // --- CALCULATIONS ---
  const calculateBillAmounts = () => {
    let billAmount = 0;
    if (tableData && Array.isArray(tableData)) {
      billAmount = tableData.reduce(
        (sum, item) => sum + (parseFloat(item.amount) || 0),
        0,
      );
    }

    // Direct Service Charge
    const serviceChargeVal = parseFloat(serviceCharge || 0);
    const totalAmount = billAmount + serviceChargeVal;

    // Direct Discount
    const discountVal = parseFloat(discount || 0);
    const netAmount = totalAmount - discountVal;

    const paid = parseFloat(paidAmount || 0);
    const payableAmount = netAmount - paid;

    return {
      billAmount: Math.round(billAmount),
      serviceChargePc: 0,
      serviceCharge: Math.round(serviceChargeVal),
      totalAmount: Math.round(totalAmount),
      discountPc: 0,
      discount: Math.round(discountVal),
      netAmount: Math.round(netAmount),
      paidAmount: Math.round(paid),
      payableAmount: Math.round(payableAmount),
    };
  };

  useEffect(() => {
    const calcs = calculateBillAmounts();
    setBillResponse((prev) => ({ ...prev, ...calcs }));
  }, [tableData, serviceCharge, discount, paidAmount]);

  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        try {
          const [patResp, agentResp, doctorResp] = await Promise.all([
            axios.get(`${BASE_URL}/patient`, {
              headers: { "x-api-key": API_KEY },
            }),
            axios.get(`${BASE_URL}/hospital/agent`, {
              headers: { "x-api-key": API_KEY },
            }),
            axios.get(`${BASE_URL}/doctor`, {
              headers: { "x-api-key": API_KEY },
            }),
          ]);

          const patientList = Array.isArray(patResp.data) ? patResp.data : [];

          setPatients(
            patientList.map((p) => ({
              value: p.id,
              label: `${p.name} (${p.phoneNumber})`,
              data: p,
            })),
          );

          setAgents(
            agentResp.data.agents.map((a) => ({
              value: a.id,
              label: a.agentName,
            })),
          );

          const rawDoctors = Array.isArray(doctorResp.data)
            ? doctorResp.data
            : doctorResp.data.data || [];
          const doctorOptions = rawDoctors.map((d) => {
            let name = d.name || "Unknown";
            if (d.translations) {
              try {
                const t =
                  typeof d.translations === "string"
                    ? JSON.parse(d.translations)
                    : d.translations;
                name = t?.en?.name || t?.bn?.name || name;
              } catch (e) {}
            }
            return { value: name, label: name };
          });
          setConsultants(doctorOptions);

          if (billId) {
            loadBillData(billId);
          } else {
            resetForm();
          }
        } catch (error) {
          console.error("Error fetching data", error);
          toast.error("Failed to load initial data");
        }
      };
      fetchData();
    }
  }, [isOpen, billId]);

  // --- HELPERS ---
  const loadBillData = async (id) => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/outdoor-bill/${id}`, {
        headers: { "x-api-key": API_KEY },
      });
      const bill = res.data.bill;
      const patient = bill.patient;

      setFormData({
        patientId: patient?.id || "",
        patientName: patient?.name || bill.patientName,
        phoneNumber: patient?.phoneNumber || "",
        admissionDate: bill.admissionDate
          ? bill.admissionDate.split("T")[0]
          : "",
        billDate: new Date(bill.createdAt).toISOString().split("T")[0],
        refer: bill.refer || "",
        referAmount: bill.referAmount || "",
        billNumber: bill.billNumber || "",
        consultantName: bill.consultantName || "",
      });

      setTableData(
        bill.items.map((item) => ({
          serviceName: item.serviceName,
          unitPrice: item.unitPrice,
          totalUnit: item.totalUnit,
          amount: item.amount,
        })),
      );

      setServiceCharge(bill.serviceCharge || "0");
      setDiscount(bill.discount || "0");
      setPaidAmount(bill.paidAmount || "0");
    } catch (error) {
      toast.error("Failed to load bill");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setCurrentBillId(null);
    setFormData({
      patientId: "",
      patientName: "",
      admissionDate: "",
      billDate: new Date().toISOString().split("T")[0],
      refer: "",
      referAmount: "",
      billNumber: "",
      consultantName: "",
    });
    setTableData([{ serviceName: "", unitPrice: 0, totalUnit: 1, amount: 0 }]);
    setServiceCharge("0");
    setDiscount("0");
    setPaidAmount("0");
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePatientSelect = async (option) => {
    if (option) {
      const p = option.data;

      setFormData((prev) => ({
        ...prev,
        patientId: p.id,
        patientName: p.name,
        phoneNumber: p.phoneNumber || "",
      }));
      setErrors((prev) => ({ ...prev, patientName: "" }));
    }
  };

  const handleTableChange = (index, field, value) => {
    const newData = [...tableData];
    newData[index][field] = value;
    if (field === "unitPrice" || field === "totalUnit") {
      const price = parseFloat(newData[index].unitPrice) || 0;
      const qty = parseFloat(newData[index].totalUnit) || 0;
      newData[index].amount = price * qty;
    }
    setTableData(newData);
  };

  const handleAddRow = () =>
    setTableData([
      ...tableData,
      { serviceName: "", unitPrice: 0, totalUnit: 1, amount: 0 },
    ]);
  const handleRemoveRow = (idx) =>
    setTableData(tableData.filter((_, i) => i !== idx));
  const handleOtherInputChange = (setter) => (e) => setter(e.target.value);

  // --- ADD PATIENT ---
  const handleAddNewPatient = async () => {
    if (!newPatientData.name || !newPatientData.phoneNumber)
      return toast.error("Name & Phone required");

    setIsAddingPatient(true);

    try {
      const res = await axios.post(`${BASE_URL}/patient/add`, newPatientData, {
        headers: { "x-api-key": API_KEY },
      });
      const p = res.data.patient;
      const newOption = {
        value: p.id,
        label: `${p.name} (${p.phoneNumber})`,
        data: p,
      };

      setPatients((prev) => [newOption, ...prev]);
      handlePatientSelect(newOption);
      setIsNewPatientOpen(false);
      setNewPatientData({ name: "", phoneNumber: "" });
      toast.success("Patient Added!");
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to add patient");
    } finally {
      setIsAddingPatient(false);
    }
  };

  const saveBillToApi = async () => {
    // Validation
    const newErrors = {};
    if (!formData.patientId) newErrors.patientName = "Patient is required";
    if (tableData.some((r) => !r.serviceName))
      newErrors.serviceTable = "Service Name required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill required fields");
      return null;
    }

    const payload = {
      ...formData,
      serviceItems: tableData,
      ...billResponse,
      serviceCharge: parseFloat(serviceCharge) || 0,
      discount: parseFloat(discount) || 0,
      paidAmount: parseFloat(paidAmount) || 0,
      serviceChargePc: 0,
      discountPc: 0,
    };

    let response;
    if (currentBillId) {
      response = await axios.put(
        `${BASE_URL}/outdoor-bill/edit/${currentBillId}`,
        payload,
        { headers: { "x-api-key": API_KEY } },
      );
    } else {
      response = await axios.post(`${BASE_URL}/outdoor-bill/add`, payload, {
        headers: { "x-api-key": API_KEY },
      });
    }
    return response.data.bill;
  };

  // --- 1. HANDLE SUBMIT (SAVE ONLY) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoadingAction("save");

    try {
      const savedBill = await saveBillToApi();
      if (savedBill) {
        toast.success("Bill Saved Successfully");
        onSave();
        onClose();
      }
    } catch (error) {
      toast.error("Failed to save bill");
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  };

  const handleSaveAndPrint = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoadingAction("print");

    try {
      const savedBill = await saveBillToApi();

      if (savedBill) {
        if (currentBillId) {
          toast.success("Bill Updated Successfully");
        } else {
          toast.success("Bill Saved Successfully");
        }
        // 2. Prepare Print Data
        const signatureFooter = (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "80px",
              paddingRight: "10px",
            }}
          >
            <div style={{ textAlign: "center", width: "220px" }}>
              <div
                style={{
                  borderTop: "1px solid #333",
                  height: "1px",
                  marginBottom: "8px",
                }}
              ></div>
              <p style={{ margin: "0", fontWeight: "bold", fontSize: "12pt" }}>
                Authorized Signature
              </p>
              <p style={{ margin: "5px 0 0 0", fontSize: "10pt" }}>
                Authorized By
              </p>
            </div>
          </div>
        );

        const billInfo = (
          <>
            <div>
              <p>
                <strong>Bill No:</strong> {savedBill.billNumber || "N/A"}
              </p>
              <p>
                <strong>Patient Name:</strong> {formData.patientName || "N/A"}
              </p>
              <p>
                <strong>Phone:</strong> {formData.phoneNumber || "N/A"}
              </p>
            </div>
            <div>
              <p>
                <strong>Admission Date:</strong>{" "}
                {formData.admissionDate
                  ? new Date(formData.admissionDate).toLocaleDateString("en-US")
                  : "N/A"}
              </p>
              <p>
                <strong>Billing Date:</strong>{" "}
                {formData.billDate
                  ? new Date(formData.billDate).toLocaleDateString("en-US")
                  : "N/A"}
              </p>
              <p>
                <strong>Consultant:</strong> {formData.consultantName || "N/A"}
              </p>
            </div>
          </>
        );

        const table = (
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 sm:p-3 border font-semibold">Sl No</th>
                <th className="p-2 sm:p-3 border font-semibold">
                  Service Name
                </th>
                <th className="p-2 sm:p-3 border font-semibold text-right">
                  Unit Price (৳)
                </th>
                <th className="p-2 sm:p-3 border font-semibold text-right">
                  Total Unit
                </th>
                <th className="p-2 sm:p-3 border font-semibold text-right">
                  Amount (৳)
                </th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => (
                <tr key={index} className="border-t">
                  <td className="p-2 sm:p-3 border">{index + 1}</td>
                  <td className="p-2 sm:p-3 border">
                    {row.serviceName || "N/A"}
                  </td>
                  <td className="p-2 sm:p-3 border text-right">
                    {parseFloat(row.unitPrice || 0).toFixed(2)}
                  </td>
                  <td className="p-2 sm:p-3 border text-right">
                    {row.totalUnit || "0"}
                  </td>
                  <td className="p-2 sm:p-3 border text-right">
                    {parseFloat(row.amount || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

        const summary = (
          <div className="flex flex-col">
            <div className="flex justify-between text-gray-600">
              <span>Total Amount:</span>
              <span>
                ৳{Math.round(billResponse?.totalAmount || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Discount:</span>
              <span>৳{Math.round(billResponse?.discount || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Net Amount:</span>
              <span>
                ৳{Math.round(billResponse?.netAmount || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Paid Amount:</span>
              <span>
                ৳{Math.round(billResponse?.paidAmount || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Payable Amount:</span>
              <span>
                ৳{Math.round(billResponse?.payableAmount || 0).toFixed(2)}
              </span>
            </div>
          </div>
        );

        const newPrintData = { billInfo, table, summary, signatureFooter };
        setPrintData(newPrintData);
        setShowPrintPreview(true);

        setTimeout(() => {
          try {
            const elementsToHide = document.querySelectorAll(
              ".print-hidden, .print-hidden-in-print",
            );
            elementsToHide.forEach((el) => (el.style.display = "none"));

            const printLayout = document.querySelector(
              ".hospital-bill-print-preview",
            );
            if (printLayout) {
              printLayout.style.display = "block";
            }

            window.print();

            setTimeout(() => {
              elementsToHide.forEach((el) => (el.style.display = ""));
              if (printLayout) {
                printLayout.style.display = "none";
              }
              setShowPrintPreview(false);
              onSave();
              onClose();
            }, 500);
          } catch (printError) {
            console.error("Print failed:", printError);
            toast.success("Bill Saved (Print interrupted)");
            onSave();
            onClose();
          }
        }, 1000);
      }
    } catch (error) {
      console.error("Save failed:", error);
      toast.error("Failed to save bill");
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white shadow-lg w-full max-w-full max-h-[100vh] h-full overflow-y-auto print-wrapper">
        {/* --- HEADER --- */}
        <div className="bg-[#ececec] p-5 flex flex-col md:flex-row justify-between items-center mb-4 border-b border-dashed border-gray-300">
          <div className="flex flex-col items-center md:items-start">
            <h2 className="text-xl font-semibold text-gray-700">
              {billId ? "Edit Outdoor Bill" : "Generate Outdoor Bill"}
            </h2>
          </div>

          <div className="flex items-start justify-center flex-wrap gap-2 mt-3 xl:mt-0 relative z-50">
            {/* Patient Search + Add Button */}
            <div className="w-80 flex items-start gap-2">
              <div className="flex-1 relative">
                <DynamicSelect
                  options={patients}
                  value={
                    patients.find((p) => p.value === formData.patientId) || null
                  }
                  onChange={handlePatientSelect}
                  placeholder="Search Patient"
                  isSearchable
                  className={`w-full rounded-md border ${
                    errors.patientName ? "border-red-500" : "border-gray-300"
                  } bg-gray-50 shadow-sm`}
                />
                {errors.patientName && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.patientName}
                  </p>
                )}
              </div>
              <button
                onClick={() => setIsNewPatientOpen(true)}
                className="bg-[#73CB64] hover:bg-M-primary-color shrink-0 text-sm p-3 rounded-md text-white transition-all shadow-sm"
                title="Add New Patient"
              >
                <FaUserPlus />
              </button>
            </div>

            <button
              title="Close"
              className="size-7 rounded-full bg-white text-M-secondary-color text-sm flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
              onClick={onClose}
              disabled={isLoading}
            >
              <RiCloseLargeFill />
            </button>
          </div>
        </div>

        <div className="my-4 px-6 h-[calc(100vh-150px)] overflow-y-auto">
          <form onSubmit={(e) => e.preventDefault()}>
            {/* Form Inputs Grid (Matches Hospital Layout) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-6">
              {/* Admission Date (Requested) */}
              <div>
                <label className="label font-medium text-black">
                  Admission Date
                </label>
                <input
                  type="date"
                  name="admissionDate"
                  value={formData.admissionDate}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Billing Date */}
              <div>
                <label className="label font-medium text-black">
                  Billing Date
                </label>
                <input
                  type="date"
                  name="billDate"
                  value={formData.billDate}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Refer By */}
              <div>
                <label className="label font-medium text-black">Refer By</label>
                <DynamicSelect
                  options={agents}
                  value={agents.find((a) => a.label === formData.refer) || null}
                  onChange={(opt) =>
                    setFormData({ ...formData, refer: opt?.label || "" })
                  }
                  placeholder="Select Referrer"
                  className="w-full rounded-md border border-gray-300 bg-gray-50 shadow-sm"
                />
              </div>

              {/* Refer Amount */}
              <div>
                <label className="label font-medium text-black">
                  Refer Amount
                </label>
                <input
                  type="number"
                  name="referAmount"
                  value={formData.referAmount}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>

              {/* Consultant Name */}
              <div>
                <label className="label font-medium text-black">
                  Consultant Name
                </label>
                <DynamicSelect
                  options={consultants}
                  value={
                    consultants.find(
                      (c) => c.label === formData.consultantName,
                    ) || null
                  }
                  onChange={(opt) =>
                    setFormData({
                      ...formData,
                      consultantName: opt?.label || "",
                    })
                  }
                  placeholder="Select Consultant"
                  className="w-full rounded-md border border-gray-300 bg-gray-50 shadow-sm"
                />
              </div>
            </div>

            {/* --- SERVICE TABLE --- */}
            <div className="overflow-x-auto mt-6">
              <h2 className="text-lg font-bold text-gray-700 mb-3">Services</h2>
              <table className="w-full table-fixed border-collapse min-w-[1000px] mb-5">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Service Name <span className="text-red-600">*</span>
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Unit Price
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Total Unit
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Amount
                    </th>
                    <th className="text-center text-xs text-black border border-gray-300 py-2 px-3 w-16 font-medium">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={index}>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="text"
                          value={row.serviceName}
                          onChange={(e) =>
                            handleTableChange(
                              index,
                              "serviceName",
                              e.target.value,
                            )
                          }
                          className="w-full p-2 border border-gray-300 rounded-md text-sm outline-none focus:border-blue-500"
                          placeholder="Enter Service Name"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="number"
                          value={row.unitPrice}
                          onChange={(e) =>
                            handleTableChange(
                              index,
                              "unitPrice",
                              e.target.value,
                            )
                          }
                          className="w-full p-2 border border-gray-300 rounded-md text-sm text-end outline-none focus:border-blue-500"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="number"
                          value={row.totalUnit}
                          onChange={(e) =>
                            handleTableChange(
                              index,
                              "totalUnit",
                              e.target.value,
                            )
                          }
                          className="w-full p-2 border border-gray-300 rounded-md text-sm text-end outline-none focus:border-blue-500"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="number"
                          value={Math.round(row.amount)}
                          readOnly
                          className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3 text-center">
                        <button
                          title="Remove"
                          onClick={() => handleRemoveRow(index)}
                          className="size-7 mx-auto rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                          disabled={tableData.length === 1}
                        >
                          <RiDeleteBinLine />
                        </button>
                      </td>
                    </tr>
                  ))}

                  {/* Totals Rows */}
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={3}
                    >
                      Bill Amount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={billResponse.billAmount}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <button
                        title="Add Row"
                        onClick={handleAddRow}
                        className="size-7 mx-auto rounded-full bg-[#73CB64] text-white text-sm flex items-center justify-center hover:bg-M-primary-color transition-all duration-300"
                      >
                        <RiAddFill />
                      </button>
                    </td>
                  </tr>
                  {/* Service Charge  */}
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={3}
                    >
                      Service Charge (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={serviceCharge}
                        onChange={handleOtherInputChange(setServiceCharge)}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end outline-none focus:border-blue-500"
                        placeholder="0"
                        min="0"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  {/* Total Amount */}
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={3}
                    >
                      Total Amount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={billResponse.totalAmount}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  {/* Discount */}
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={3}
                    >
                      Discount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={discount}
                        onChange={handleOtherInputChange(setDiscount)}
                        readOnly={!!currentBillId}
                        className={`w-full p-2 border rounded-md text-sm text-end focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.discount ? "border-red-500" : "border-gray-300"
                        }
                          ${currentBillId ? "bg-gray-100 cursor-not-allowed" : ""}
                          `}
                        placeholder="0"
                        min="0"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  {/* Net Amount */}
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={3}
                    >
                      Net Amount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={billResponse.netAmount}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  {/* Paid */}
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={3}
                    >
                      Paid (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={paidAmount}
                        onChange={(e) => setPaidAmount(e.target.value)}
                        readOnly={!!currentBillId}
                        className={`w-full p-2 border rounded-md text-sm text-end focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.paidAmount
                            ? "border-red-500"
                            : "border-gray-300"
                        }
                          ${currentBillId ? "bg-gray-100 cursor-not-allowed" : ""}
                          `}
                        placeholder="0"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  {/* Payable */}
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={3}
                    >
                      Payable (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={billResponse.payableAmount}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </form>
        </div>

        {/* --- FOOTER --- */}
        <div className="flex justify-end items-center gap-3 px-6 fixed w-full bottom-0 p-3 bg-gray-200 z-20 border-t border-dashed border-M-text-color/50">
          <div className="flex gap-2">
            <button
              onClick={resetForm}
              className="bg-gray-600 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-gray-800 transition-all duration-300"
              disabled={isLoading}
            >
              Reset
            </button>
            <button
              onClick={onClose}
              className="bg-[#E7633D]/90 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveAndPrint}
              className="bg-M-primary-color/90 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              disabled={isLoading}
            >
              {loadingAction === "print"
                ? "Saving..."
                : currentBillId
                  ? "Update & Print"
                  : "Save & Print"}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="bg-M-primary-color/90 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              disabled={isLoading}
            >
              {loadingAction === "save"
                ? "Saving..."
                : currentBillId
                  ? "Update"
                  : "Save"}
            </button>
          </div>
        </div>

        {/* --- PRINT PREVIEW --- */}
        {showPrintPreview && printData && (
          <div className="hospital-bill-print-preview">
            <PrintBillLayout
              className="hospital-bill-print-content"
              title="Outdoor Patient Bill"
              billInfo={printData.billInfo}
              table={printData.table}
              summary={printData.summary}
            />
          </div>
        )}

        {/* --- NEW PATIENT MODAL --- */}
        {isNewPatientOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0">
            <div className="bg-white rounded-lg shadow-lg p-6 w-96">
              <h2 className="text-xl font-bold text-gray-700 mb-4">
                Add New Patient
              </h2>
              <div className="mb-4">
                <label className="label block text-sm font-medium mb-1 text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={newPatientData.name}
                  onChange={(e) =>
                    setNewPatientData({
                      ...newPatientData,
                      name: e.target.value,
                    })
                  }
                  className={`w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter patient name"
                />
              </div>
              <div className="mb-4">
                <label className="label block text-sm font-medium mb-1 text-gray-700">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={newPatientData.phoneNumber}
                  onChange={(e) =>
                    setNewPatientData({
                      ...newPatientData,
                      phoneNumber: e.target.value,
                    })
                  }
                  className={`w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter phone number"
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setIsNewPatientOpen(false)}
                  className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddNewPatient}
                  className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={isAddingPatient}
                >
                  {isAddingPatient ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Adding...
                    </>
                  ) : (
                    "Add Patient"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutdoorBillModal;
